import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from "react";
import { makeStyles, Select, MenuItem, Tooltip } from "@material-ui/core";
const InfoIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{ cursor: "pointer", flexShrink: 0 }}
  >
    <path
      d="M14.1666 7.9987C14.1666 4.59294 11.4057 1.83203 7.99998 1.83203C4.59422 1.83203 1.83331 4.59294 1.83331 7.9987C1.83331 11.4045 4.59422 14.1654 7.99998 14.1654C11.4057 14.1654 14.1666 11.4045 14.1666 7.9987ZM7.49998 10.6654V7.9987C7.49998 7.72256 7.72384 7.4987 7.99998 7.4987C8.27612 7.4987 8.49998 7.72256 8.49998 7.9987V10.6654C8.49998 10.9415 8.27612 11.1654 7.99998 11.1654C7.72384 11.1654 7.49998 10.9415 7.49998 10.6654ZM8.00649 4.83203C8.28263 4.83203 8.50649 5.05589 8.50649 5.33203C8.50649 5.60817 8.28263 5.83203 8.00649 5.83203H7.99998C7.72384 5.83203 7.49998 5.60817 7.49998 5.33203C7.49998 5.05589 7.72384 4.83203 7.99998 4.83203H8.00649ZM15.1666 7.9987C15.1666 11.9567 11.958 15.1654 7.99998 15.1654C4.04194 15.1654 0.833313 11.9567 0.833313 7.9987C0.833313 4.04066 4.04194 0.832031 7.99998 0.832031C11.958 0.832031 15.1666 4.04066 15.1666 7.9987Z"
      fill="#222336"
    />
  </svg>
);
import { useParams } from "react-router-dom";
import { apiClient } from "module/api";
import { SnackBarContext } from "components/snack-bar";
import TrashIcon from "components/icons/components/TrashIcon";
import { DEFAULT_PLACEHOLDERS } from "./const";
import { sanitizeVariableName } from "./utils";
import { useVariables } from "./context/VariablesContext";
import { useVariablesApi } from "./hooks/useVariablesApi";
import { parseFile, downloadTemplate } from "./utils/fileParser";
import ColumnMappingModal from "./components/ColumnMappingModal";
import PreviewVariablesModal from "./components/PreviewVariablesModal";
import { matchVariables } from "./utils/variableMatching";
import { DropdownChevronIcon } from "pages/flyer/builder/drawer/components/Primitives/icons/DropdownChevronIcon";
import { useAudienceData } from "./hooks/useAudienceData";
import { QR_VARIABLE_NAMES } from "./hooks/useTldrawIntegration";

/**
 * A QR code placeholder should NOT be auto-removed when the user deselects or
 * switches the CSV file (or uploads a new one). The user removes it manually
 * via the chip's X button if they want to. This is different from every other
 * placeholder, which is cleaned up when it's not in the active file anymore.
 */
const isQrPlaceholder = (name: string): boolean => QR_VARIABLE_NAMES.includes(sanitizeVariableName(name));
import type { ColumnMapping, VariableFile, UploadedFileData } from "./types";

const UPLOAD_NEW_VALUE = "__upload_new__";
const SELECT_NONE_VALUE = "__none__";

const DropdownChevron = (props: any) => <DropdownChevronIcon {...props} />;

const selectMenuProps = {
  PaperProps: {
    style: {
      borderRadius: 8,
      border: "1px solid #E1E6EB",
      boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
      marginTop: -8,
      maxHeight: 500,
    },
  },
  getContentAnchorEl: null as any,
  anchorOrigin: { vertical: "bottom" as const, horizontal: "left" as const },
  transformOrigin: { vertical: "top" as const, horizontal: "left" as const },
  disableAutoFocusItem: true,
};

/** Format bytes to human-readable size */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

const VariablesPanelV2 = ({
  onAddVariable,
  onRemoveVariable,
  registerVariableRemovalCallback,
  getActiveVariablesFromCanvas,
  registerVariablesChangeCallback,
  applyPreview,
  clearPreview,
  editor,
}: {
  onAddVariable: (variableName: string) => void;
  onRemoveVariable: (variableName: string) => void;
  registerVariableRemovalCallback?: (callback: (variableName: string) => void) => void;
  getActiveVariablesFromCanvas?: () => string[];
  registerVariablesChangeCallback?: (callback: () => void) => void;
  applyPreview?: (previewValues: Record<string, string>) => void;
  clearPreview?: () => void;
  editor?: any;
}) => {
  const classes = useStyles();
  const runSnackBar = useContext(SnackBarContext);
  const {
    campaignId,
    roomId: roomIdParam,
    format,
  } = useParams() as {
    campaignId?: string;
    roomId?: string;
    format?: string;
  };
  // Match Settings.tsx logic: read designId from query string first, then route params
  const designId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("designId") || roomIdParam;
  }, [roomIdParam]);

  const {
    selectedVariableFile,
    setSelectedVariableFile,
    existingVariableFiles,
    uploadedFileData,
    setUploadedFileData,
    columnMappings,
    setColumnMappings,
    matchingResult,
    setMatchingResult,
    isLoading,
    // Persistent selection state (survives tab switches)
    selectedDefaultPlaceholders,
    setSelectedDefaultPlaceholders,
    selectedCustomVariables,
    setSelectedCustomVariables,
    selectedExistingPlaceholders,
    setSelectedExistingPlaceholders,
    selectedAudiencePlaceholders,
    setSelectedAudiencePlaceholders,
    customPlaceholders,
    setCustomPlaceholders,
    existingFileData,
    setExistingFileData,
  } = useVariables();

  const { fetchVariables, uploadVariable, downloadAndParseCSV } = useVariablesApi();
  const {
    isADM,
    audiences: campaignAudiences,
    loading: audienceLoading,
    audienceCsvData,
    fetchAudienceCsvData,
  } = useAudienceData();

  // Transient UI state (OK to reset on tab switch)
  const [isAddNew, setIsAddNew] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [pendingFileData, setPendingFileData] = useState<UploadedFileData | null>(null);
  const [dragging, setDragging] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  // Upload progress tracking
  const [uploadProgress, setUploadProgress] = useState<{
    active: boolean;
    fileName: string;
    fileSize: number;
    percent: number;
  } | null>(null);
  const uploadAbortRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const handleFileUploadRef = useRef<(file: File) => void>(() => {});

  // Register native drag/drop event listeners on the dropzone
  // (React synthetic events don't stopPropagation before tldraw's native body listeners)
  useEffect(() => {
    const el = dropZoneRef.current;
    if (!el) return;

    const nativeDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      setDragging(true);
    };
    const nativeDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (el.contains(e.relatedTarget as Node)) return;
      setDragging(false);
    };
    const nativeDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const nativeDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) handleFileUploadRef.current(file);
    };

    el.addEventListener("dragover", nativeDragOver);
    el.addEventListener("dragleave", nativeDragLeave);
    el.addEventListener("dragenter", nativeDragEnter);
    el.addEventListener("drop", nativeDrop);

    return () => {
      el.removeEventListener("dragover", nativeDragOver);
      el.removeEventListener("dragleave", nativeDragLeave);
      el.removeEventListener("dragenter", nativeDragEnter);
      el.removeEventListener("drop", nativeDrop);
    };
    // Re-register when uploadProgress changes — the dropzone div remounts after upload/cancel
  }, [uploadProgress]);

  // Dropdown value: file id or "" (empty)
  const dropdownValue = selectedVariableFile ? String(selectedVariableFile.id) : uploadedFileData ? "__uploaded__" : "";

  // Sync state with canvas — only syncs DEFAULT placeholders on every canvas change.
  // Existing and custom variable selections are managed by their own dropdowns
  // and only synced on mount (initial load).
  const syncStateWithCanvas = useCallback(() => {
    if (!getActiveVariablesFromCanvas) return;
    const activeVariables = getActiveVariablesFromCanvas();
    const defaultPlaceholderNames = DEFAULT_PLACEHOLDERS.map((p) => p.name);

    // Build a set of all non-default selected placeholders (from CSV, audience, existing file)
    // to avoid duplicating them as defaults (e.g., first_name from CSV → "First Name" from canvas scan)
    const nonDefaultSelected = new Set([
      ...selectedAudiencePlaceholders,
      ...selectedExistingPlaceholders,
      ...selectedCustomVariables,
    ]);

    // Also build sanitized versions for comparison
    const nonDefaultSanitized = new Set(Array.from(nonDefaultSelected).map((n) => sanitizeVariableName(n)));

    setSelectedDefaultPlaceholders(
      activeVariables.filter((name) => {
        if (!defaultPlaceholderNames.includes(name)) return false;
        const sanitized = sanitizeVariableName(name);
        if (nonDefaultSanitized.has(sanitized)) return false;
        return true;
      })
    );

    // Remove chips for variables that are no longer on the canvas
    const activeSanitized = new Set(activeVariables.map((v) => sanitizeVariableName(v)));
    setSelectedCustomVariables((prev) => prev.filter((p) => activeSanitized.has(sanitizeVariableName(p))));
    setSelectedExistingPlaceholders((prev) => prev.filter((p) => activeSanitized.has(sanitizeVariableName(p))));
    setSelectedAudiencePlaceholders((prev) => prev.filter((p) => activeSanitized.has(sanitizeVariableName(p))));

    // Sync qr_link selection with canvas: auto-select when QR exists, remove when deleted
    const hasQrOnCanvas = activeVariables.some((v) => sanitizeVariableName(v) === "qr_link");
    const isQrLink = (name: string) => sanitizeVariableName(name) === "qr_link";
    const qrAlreadySelected =
      selectedCustomVariables.some(isQrLink) ||
      selectedExistingPlaceholders.some(isQrLink) ||
      selectedAudiencePlaceholders.some(isQrLink);

    if (hasQrOnCanvas && !qrAlreadySelected) {
      // QR on canvas but not selected — find the right dropdown and add it
      const allUploadedCols = uploadedFileData?.columns || [];
      const allExistingCols = selectedVariableFile?.csvColumns || [];
      const allAudienceHeaders = isADM ? campaignAudiences.flatMap((a: any) => a.headers) : [];

      const qrInUploaded = allUploadedCols.find((c: string) => sanitizeVariableName(c) === "qr_link");
      const qrInExisting = allExistingCols.find((c: string) => sanitizeVariableName(c) === "qr_link");
      const qrInAudience = allAudienceHeaders.find((c: string) => sanitizeVariableName(c) === "qr_link");

      if (qrInUploaded) {
        setSelectedCustomVariables((prev) => (prev.includes(qrInUploaded) ? prev : [...prev, qrInUploaded]));
      } else if (qrInExisting) {
        setSelectedExistingPlaceholders((prev) => (prev.includes(qrInExisting) ? prev : [...prev, qrInExisting]));
      } else if (qrInAudience) {
        setSelectedAudiencePlaceholders((prev) => (prev.includes(qrInAudience) ? prev : [...prev, qrInAudience]));
      }
    } else if (!hasQrOnCanvas && qrAlreadySelected) {
      // QR removed from canvas — clean up from all selection arrays
      setSelectedCustomVariables((prev) => {
        const filtered = prev.filter((p) => !isQrLink(p));
        return filtered.length === prev.length ? prev : filtered;
      });
      setSelectedExistingPlaceholders((prev) => {
        const filtered = prev.filter((p) => !isQrLink(p));
        return filtered.length === prev.length ? prev : filtered;
      });
      setSelectedAudiencePlaceholders((prev) => {
        const filtered = prev.filter((p) => !isQrLink(p));
        return filtered.length === prev.length ? prev : filtered;
      });
    }
  }, [
    getActiveVariablesFromCanvas,
    selectedAudiencePlaceholders,
    selectedExistingPlaceholders,
    selectedCustomVariables,
    uploadedFileData,
    selectedVariableFile,
    isADM,
    campaignAudiences,
  ]);

  // On first mount only: sync existing and custom placeholders with canvas
  // Skip if selections already exist in context (returning from tab switch)
  const initialSyncDone = useRef(false);
  const hasExistingSelections =
    selectedDefaultPlaceholders.length > 0 ||
    selectedCustomVariables.length > 0 ||
    selectedExistingPlaceholders.length > 0 ||
    selectedAudiencePlaceholders.length > 0 ||
    customPlaceholders.length > 0;

  useEffect(() => {
    if (initialSyncDone.current || hasExistingSelections || !getActiveVariablesFromCanvas) return;
    initialSyncDone.current = true;
    const activeVariables = getActiveVariablesFromCanvas();
    const defaultPlaceholderNames = DEFAULT_PLACEHOLDERS.map((p) => p.name);
    const currentFilePlaceholders = selectedVariableFile?.csvColumns || [];
    const currentUploadedPlaceholders = uploadedFileData?.columns || [];

    if (currentFilePlaceholders.length > 0) {
      const matchingExisting = currentFilePlaceholders.filter((col) => {
        const sanitizedCol = sanitizeVariableName(col);
        return activeVariables.some((v) => sanitizeVariableName(v) === sanitizedCol);
      });
      setSelectedExistingPlaceholders(matchingExisting);
    }
    if (currentUploadedPlaceholders.length > 0) {
      const matchingUploaded = currentUploadedPlaceholders.filter((col) => {
        const sanitizedCol = sanitizeVariableName(col);
        return activeVariables.some((v) => sanitizeVariableName(v) === sanitizedCol);
      });
      setSelectedCustomVariables(matchingUploaded);
    }
    // Sync audience placeholders
    const audienceHeaders = Array.from(new Set(campaignAudiences.flatMap((a) => a.headers)));
    if (audienceHeaders.length > 0) {
      const matchingAudience = audienceHeaders.filter((col) => {
        const sanitizedCol = sanitizeVariableName(col);
        return activeVariables.some((v) => sanitizeVariableName(v) === sanitizedCol);
      });
      setSelectedAudiencePlaceholders(matchingAudience);
    }

    setCustomPlaceholders(
      activeVariables.filter(
        (name) =>
          !defaultPlaceholderNames.includes(name) &&
          !currentFilePlaceholders.includes(name) &&
          !currentUploadedPlaceholders.includes(name) &&
          !audienceHeaders.includes(name)
      )
    );
  }, [getActiveVariablesFromCanvas, selectedVariableFile, uploadedFileData, campaignAudiences]);

  useEffect(() => {
    syncStateWithCanvas();
  }, [syncStateWithCanvas]);

  useEffect(() => {
    if (registerVariablesChangeCallback) {
      registerVariablesChangeCallback(syncStateWithCanvas);
    }
  }, [registerVariablesChangeCallback, syncStateWithCanvas]);

  useEffect(() => {
    if (registerVariableRemovalCallback) {
      registerVariableRemovalCallback((variableName: string) => {
        const sanitizedRemoved = sanitizeVariableName(variableName);
        // Remove from all selection arrays — match by exact name OR sanitized name
        const matchesName = (p: string) => p === variableName || sanitizeVariableName(p) === sanitizedRemoved;
        setSelectedDefaultPlaceholders((prev) => prev.filter((p) => !matchesName(p)));
        setSelectedExistingPlaceholders((prev) => prev.filter((p) => !matchesName(p)));
        setSelectedCustomVariables((prev) => prev.filter((p) => !matchesName(p)));
        setCustomPlaceholders((prev) => prev.filter((p) => !matchesName(p)));
        setSelectedAudiencePlaceholders((prev) => prev.filter((p) => !matchesName(p)));
      });
    }
  }, [registerVariableRemovalCallback]);

  // Fetch existing variables on mount
  useEffect(() => {
    fetchVariables();
  }, [fetchVariables]);

  // Auto-fetch audience CSV data when audiences are loaded (for combined preview)
  useEffect(() => {
    if (isADM && campaignAudiences.length > 0 && !audienceCsvData) {
      fetchAudienceCsvData();
    }
  }, [isADM, campaignAudiences, audienceCsvData, fetchAudienceCsvData]);

  // Get placeholders from selected file or uploaded data
  const getFilePlaceholders = useCallback((): string[] => {
    if (selectedVariableFile) return selectedVariableFile.csvColumns;
    if (uploadedFileData) return uploadedFileData.columns;
    return [];
  }, [selectedVariableFile, uploadedFileData]);

  // Auto-match variables
  useEffect(() => {
    const placeholders = getFilePlaceholders();
    // Only run matching when there's a file with placeholders
    if (placeholders.length === 0) {
      setMatchingResult(null);
      return;
    }

    const canvasVars = getActiveVariablesFromCanvas ? getActiveVariablesFromCanvas() : [];

    if (canvasVars.length === 0) {
      setMatchingResult({ matched: [], unmatched: placeholders });
      runSnackBar({
        msg: "No placeholders detected.",
        type: "info",
        vertical: "bottom",
        horizontal: "right",
      });
      return;
    }

    const result = matchVariables(canvasVars, placeholders);
    setMatchingResult(result);

    // When a file is active, clean up placeholders that are NOT in the new file
    const ALWAYS_KEEP = ["Full Name"];
    const alwaysKeepSanitized = new Set(ALWAYS_KEEP.map((n) => sanitizeVariableName(n)));
    const placeholdersSanitized = new Set(placeholders.map((p: string) => sanitizeVariableName(p)));

    // Remove default placeholders not in the new file (except ALWAYS_KEEP
    // and QR placeholders, which are preserved on CSV change).
    const defaultsToRemove = selectedDefaultPlaceholders.filter((p) => {
      const san = sanitizeVariableName(p);
      if (alwaysKeepSanitized.has(san)) return false;
      if (placeholdersSanitized.has(san)) return false;
      if (isQrPlaceholder(p)) return false;
      return true;
    });
    if (defaultsToRemove.length > 0) {
      defaultsToRemove.forEach((v) => onRemoveVariable(v));
      setSelectedDefaultPlaceholders((prev) => prev.filter((p) => !defaultsToRemove.includes(p)));
    }

    // Remove custom variables from previous file that are NOT in the new file
    // (QR placeholders are preserved on CSV change).
    const customToRemove = selectedCustomVariables.filter((p) => {
      const san = sanitizeVariableName(p);
      if (placeholdersSanitized.has(san)) return false;
      if (isQrPlaceholder(p)) return false;
      return true;
    });
    if (customToRemove.length > 0) {
      customToRemove.forEach((v) => onRemoveVariable(v));
      setSelectedCustomVariables((prev) => prev.filter((p) => !customToRemove.includes(p)));
    }

    // Remove existing placeholders from previous file that are NOT in the new
    // file (QR placeholders are preserved on CSV change).
    const existingToRemove = selectedExistingPlaceholders.filter((p) => {
      const san = sanitizeVariableName(p);
      if (placeholdersSanitized.has(san)) return false;
      if (isQrPlaceholder(p)) return false;
      return true;
    });
    if (existingToRemove.length > 0) {
      existingToRemove.forEach((v) => onRemoveVariable(v));
      setSelectedExistingPlaceholders((prev) => prev.filter((p) => !existingToRemove.includes(p)));
    }

    // Pre-select matched variables in the custom variables dropdown
    if (result.matched.length > 0) {
      setSelectedCustomVariables(result.matched);
      // Remove matched items from defaults to avoid double-counting
      const matchedSanitized = new Set(result.matched.map((m: string) => sanitizeVariableName(m)));
      setSelectedDefaultPlaceholders((prev) => prev.filter((p) => !matchedSanitized.has(sanitizeVariableName(p))));
      runSnackBar({
        msg: `${result.matched.length} placeholders matched`,
        type: "success",
        vertical: "bottom",
        horizontal: "right",
      });
    } else {
      runSnackBar({
        msg: "No placeholders detected.",
        type: "info",
        vertical: "bottom",
        horizontal: "right",
      });
    }
  }, [
    selectedVariableFile,
    uploadedFileData,
    getActiveVariablesFromCanvas,
    getFilePlaceholders,
    setMatchingResult,
    runSnackBar,
  ]);

  // Handlers
  const handleSelectDefaultPlaceholder = (placeholder: string) => {
    setSelectedDefaultPlaceholders((prev) => [...prev, placeholder]);
    onAddVariable(placeholder);
  };

  const handleRemoveDefaultPlaceholder = (placeholder: string) => {
    setSelectedDefaultPlaceholders((prev) => prev.filter((p) => p !== placeholder));
    onRemoveVariable(placeholder);
  };

  const handleDropdownChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const value = e.target.value as string;
    if (value === UPLOAD_NEW_VALUE) {
      fileInputRef.current?.click();
    } else if (value === SELECT_NONE_VALUE || value === "") {
      // Deselect current file — move matching placeholders back to defaults
      const defaultNames = DEFAULT_PLACEHOLDERS.map((p) => p.name);
      const defaultSanitized = new Set(defaultNames.map((n) => sanitizeVariableName(n)));
      const prevSelected = [...selectedCustomVariables, ...selectedExistingPlaceholders];
      const toRemove: string[] = [];

      prevSelected.forEach((v) => {
        const san = sanitizeVariableName(v);
        // Preserve QR placeholders when deselecting the CSV file — the QR code
        // shape on the canvas should not be removed by a file deselection.
        if (isQrPlaceholder(v)) return;
        if (!defaultSanitized.has(san)) {
          toRemove.push(v);
        }
      });

      toRemove.forEach((v) => onRemoveVariable(v));
      setSelectedCustomVariables([]);
      setSelectedExistingPlaceholders([]);
      setSelectedVariableFile(null);
      setUploadedFileData(null);
      setExistingFileData([]);
      setMatchingResult(null);

      // Re-sync defaults from canvas
      setTimeout(() => {
        if (!getActiveVariablesFromCanvas) return;
        const activeVars = getActiveVariablesFromCanvas();
        const defNames = DEFAULT_PLACEHOLDERS.map((p) => p.name);
        setSelectedDefaultPlaceholders(activeVars.filter((name) => defNames.includes(name)));
      }, 50);
    } else {
      const file = existingVariableFiles.find((f) => String(f.id) === value);
      if (file) {
        // Smart transition: keep placeholders that exist in the new file or in defaults
        const newColumns = file.csvColumns || [];
        const newColsSanitized = new Set(newColumns.map((c: string) => sanitizeVariableName(c)));
        const defaultNames = DEFAULT_PLACEHOLDERS.map((p) => p.name);
        const defaultSanitized = new Set(defaultNames.map((n) => sanitizeVariableName(n)));

        // Gather all currently selected placeholders from previous file
        const prevSelected = [...selectedCustomVariables, ...selectedExistingPlaceholders];
        const toRemove: string[] = [];
        const toKeepAsDefault: string[] = [];

        prevSelected.forEach((v) => {
          const san = sanitizeVariableName(v);
          if (newColsSanitized.has(san)) {
            // Exists in new file — will be re-matched by auto-match
          } else if (defaultSanitized.has(san)) {
            // Exists in defaults — move to defaults
            toKeepAsDefault.push(defaultNames.find((d) => sanitizeVariableName(d) === san) || v);
          } else if (isQrPlaceholder(v)) {
            // Preserve QR placeholders when switching CSV files — the QR code
            // shape on the canvas should not be removed by a file change.
          } else {
            // Not in new file or defaults — remove from canvas
            toRemove.push(v);
          }
        });

        toRemove.forEach((v) => onRemoveVariable(v));
        setSelectedCustomVariables([]);
        setSelectedExistingPlaceholders([]);
        if (toKeepAsDefault.length > 0) {
          setSelectedDefaultPlaceholders((prev) => {
            const existing = new Set(prev.map((p) => sanitizeVariableName(p)));
            const newDefaults = toKeepAsDefault.filter((d) => !existing.has(sanitizeVariableName(d)));
            return [...prev, ...newDefaults];
          });
        }
        setSelectedVariableFile(file);
        setUploadedFileData(null);
        setExistingFileData([]);

        // Auto-select existing placeholders that match canvas variables
        if (getActiveVariablesFromCanvas && file.csvColumns.length > 0) {
          const canvasVars = getActiveVariablesFromCanvas();
          // Compare sanitized names since canvas vars may be "First Name" while csvColumns has "first_name"
          const matching = file.csvColumns.filter((col) => {
            const sanitizedCol = sanitizeVariableName(col);
            return canvasVars.some((v) => sanitizeVariableName(v) === sanitizedCol);
          });
          setSelectedExistingPlaceholders(matching);
        } else {
          setSelectedExistingPlaceholders([]);
        }

        // ADM row count validation for existing files (use metadata.rowsAmount, not parsed data length)
        const fileRowCount = file.metadata?.rowsAmount;
        if (audienceRowCount !== null && fileRowCount && fileRowCount !== audienceRowCount) {
          runSnackBar({
            type: "error",
            msg: `Row count mismatch: selected file has ${fileRowCount.toLocaleString()} rows but the audience has ${audienceRowCount.toLocaleString()} rows. Both files must have the same number of rows.`,
          });
          // Deselect the file
          setSelectedVariableFile(null);
          setExistingFileData([]);
          setSelectedExistingPlaceholders([]);
          return;
        }

        // Download and parse CSV for preview (only first ~100 rows)
        if (file.csvUrl) {
          downloadAndParseCSV(file.csvUrl).then((data) => {
            if (data) {
              setExistingFileData(data);
            }
          });
        }
      }
    }
  };

  // Get expected row count from audience (for ADM row validation)
  const audienceRowCount =
    isADM && campaignAudiences.length > 0 ? campaignAudiences.reduce((sum, a) => sum + a.rowsAmount, 0) : null;

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const parsed = await parseFile(file);

      // ADM row count validation (use totalRows from streaming count, not parsed sample length)
      const uploadedRowCount = parsed.totalRows ?? parsed.data.length;
      if (audienceRowCount !== null && uploadedRowCount !== audienceRowCount) {
        runSnackBar({
          type: "error",
          msg: `Row count mismatch: uploaded file has ${uploadedRowCount.toLocaleString()} rows but the audience has ${audienceRowCount.toLocaleString()} rows. Both files must have the same number of rows.`,
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      setPendingFileData(parsed);
      setShowMappingModal(true);
    } catch (error) {
      console.error("Error parsing file:", error);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Keep the ref in sync so native drop handler can call the latest handleFileUpload
  handleFileUploadRef.current = (file: File) => {
    const fakeEvent = { target: { files: [file] } } as any;
    handleFileUpload(fakeEvent);
  };

  const handleMappingConfirm = async (mappings: ColumnMapping[]) => {
    setShowMappingModal(false);
    setColumnMappings(mappings);
    if (pendingFileData) {
      // Prepare mapped data but DON'T set it yet — wait until upload completes
      const mappedData: UploadedFileData = {
        ...pendingFileData,
        columns: mappings.map((m) => m.variableName),
        data: pendingFileData.data.map((row) => {
          const mappedRow: Record<string, string> = {};
          mappings.forEach((m) => {
            mappedRow[m.variableName] = row[m.originalName] || "";
          });
          return mappedRow;
        }),
      };
      setSelectedVariableFile(null);

      // Start upload progress
      const abortController = new AbortController();
      uploadAbortRef.current = abortController;
      setUploadProgress({
        active: true,
        fileName: pendingFileData.fileName,
        fileSize: pendingFileData.csvBlob.size,
        percent: 0,
      });

      // Simulate progress since fetch doesn't support upload progress natively
      let currentPercent = 0;
      const progressInterval = setInterval(() => {
        if (abortController.signal.aborted) {
          clearInterval(progressInterval);
          return;
        }
        currentPercent = Math.min(currentPercent + Math.random() * 15 + 3, 90);
        setUploadProgress((prev) => (prev ? { ...prev, percent: Math.round(currentPercent) } : null));
      }, 300);

      try {
        const result = await uploadVariable(
          pendingFileData.csvBlob,
          pendingFileData.fileName,
          mappings,
          pendingFileData.data,
          pendingFileData.totalRows,
          abortController.signal
        );

        clearInterval(progressInterval);
        setUploadProgress((prev) => (prev ? { ...prev, percent: 100 } : null));

        // Brief delay to show 100% before clearing
        await new Promise((r) => setTimeout(r, 400));

        // Only set uploaded data if the upload wasn't cancelled
        if (!abortController.signal.aborted) {
          setUploadedFileData(mappedData);

          if (result) {
            setSelectedVariableFile(result);
          }
        }
      } catch (err) {
        clearInterval(progressInterval);
        // If aborted, ensure uploaded file data is cleared
        setUploadedFileData(null);
        setSelectedVariableFile(null);
      } finally {
        setUploadProgress(null);
        uploadAbortRef.current = null;
      }

      setPendingFileData(null);
    }
  };

  const handleMappingCancel = () => {
    setShowMappingModal(false);
    setPendingFileData(null);
  };

  const filePlaceholders = getFilePlaceholders();
  const hasFileData =
    (uploadedFileData?.data?.length ?? 0) > 0 ||
    (selectedVariableFile && filePlaceholders.length > 0) ||
    (isADM && campaignAudiences.length > 0 && selectedAudiencePlaceholders.length > 0);

  /**
   * Returns combined preview data merging audience CSV + variable file CSV row-by-row.
   * In ADM mode with both sources, each row gets fields from both files.
   * E.g., audience has {first_name, city}, variable file has {promo_code} → merged row has all three.
   */
  const getCombinedPreviewData = useCallback((): Record<string, string>[] => {
    const variableData =
      (uploadedFileData?.data?.length ?? 0) > 0
        ? uploadedFileData!.data
        : existingFileData.length > 0
        ? existingFileData
        : [];

    const audData = audienceCsvData && audienceCsvData.length > 0 ? audienceCsvData : [];

    // If both exist, merge row-by-row
    if (variableData.length > 0 && audData.length > 0) {
      const maxRows = Math.max(variableData.length, audData.length);
      const merged: Record<string, string>[] = [];
      for (let i = 0; i < maxRows; i++) {
        merged.push({
          ...(audData[i] || audData[audData.length - 1] || {}),
          ...(variableData[i] || variableData[variableData.length - 1] || {}),
        });
      }
      return merged;
    }

    // Only one source
    if (variableData.length > 0) return variableData;
    if (audData.length > 0) return audData;
    return [];
  }, [uploadedFileData, existingFileData, audienceCsvData]);

  // Generate blurred thumbnail of the canvas when file data is available.
  // Regenerates on: component mount (tab switch) and every auto-save (~500ms debounced in useMultiplayerState).
  const [thumbnailVersion, setThumbnailVersion] = useState(0);

  // Increment version on mount (handles tab switches)
  useEffect(() => {
    setThumbnailVersion((v) => v + 1);
  }, []);

  // Listen for "flyer-builder-save" custom event dispatched by useMultiplayerState on each auto-save
  useEffect(() => {
    const handleSave = () => setThumbnailVersion((v) => v + 1);
    window.addEventListener("flyer-builder-save", handleSave);
    return () => window.removeEventListener("flyer-builder-save", handleSave);
  }, []);

  // Generate thumbnail when version changes
  useEffect(() => {
    if (!hasFileData || !editor) {
      setThumbnailUrl(null);
      return;
    }

    const generateThumbnail = async () => {
      // Small delay to ensure editor is ready after remount
      await new Promise((r) => setTimeout(r, 100));
      try {
        const allIds = editor.getSortedChildIdsForParent(editor.getCurrentPageId());
        if (allIds.length === 0) return;

        // Try with all shapes first
        let result: any = null;
        try {
          result = await editor.getSvgString(allIds);
        } catch {
          // If that fails (e.g. QR shapes crash), filter out problematic shapes and retry
          const safeIds = allIds.filter((id: any) => {
            const shape = editor.getShape(id);
            if (!shape || shape.type !== "customImage") return true;
            const sid = (shape.id as string) || "";
            const assetId = (shape as any).props?.assetId?.toString() || "";
            return !(sid.includes("qrCode") || assetId.includes("qr") || (shape as any).props?.qrCodeLink);
          });
          if (safeIds.length > 0) {
            try {
              result = await editor.getSvgString(safeIds);
            } catch (e2) {
              console.error("Thumbnail fallback also failed:", e2);
            }
          }
        }

        if (result?.svg) {
          const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(result.svg)}`;
          setThumbnailUrl(url);
        }
      } catch (e) {
        console.error("Thumbnail generation failed:", e);
      }
    };
    generateThumbnail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFileData, editor, thumbnailVersion]);

  return (
    <div className={classes.panelContainer} data-tour="variables-panel">
      {/* Panel title */}
      <h2 className={classes.panelTitle} data-tour="variables-title">
        Variables
      </h2>

      {/* ============================================================ */}
      {/* 1. DEFAULT PLACEHOLDERS (first) */}
      {/* ============================================================ */}
      <div className={classes.section}>
        <div className={classes.titleRow}>
          <span className={classes.title}>
            {uploadedFileData || selectedVariableFile || (isADM && campaignAudiences.length > 0)
              ? "Placeholders"
              : "Default Placeholders"}
          </span>
          <Tooltip
            title={
              <span>
                {"• Built-in placeholders you can use when you don't have a placeholder file."}
                <br />
                <br />
                {"• Make sure your placeholders file includes these fields as column headers when uploading."}
              </span>
            }
            arrow
            placement="top"
            classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
          >
            <span style={{ display: "inline-flex" }}>
              <InfoIcon />
            </span>
          </Tooltip>
        </div>

        {/* Default placeholders dropdown */}
        <Select
          variant="outlined"
          displayEmpty
          multiple
          value={[
            ...selectedDefaultPlaceholders,
            ...selectedAudiencePlaceholders,
            ...selectedExistingPlaceholders,
            ...selectedCustomVariables,
          ]}
          onChange={(e) => {
            const newValue = e.target.value as string[];
            const allCurrentSelected = [
              ...selectedDefaultPlaceholders,
              ...selectedAudiencePlaceholders,
              ...selectedExistingPlaceholders,
              ...selectedCustomVariables,
            ];
            const added = newValue.filter((v) => !allCurrentSelected.includes(v));
            const removed = allCurrentSelected.filter((v) => !newValue.includes(v));

            const defaultNames = DEFAULT_PLACEHOLDERS.map((p) => p.name);
            const audienceHeaders = isADM ? Array.from(new Set(campaignAudiences.flatMap((a) => a.headers))) : [];
            const existingColumns = selectedVariableFile?.csvColumns || [];
            const uploadedColumns = uploadedFileData?.columns || [];

            added.forEach((p) => {
              if (defaultNames.includes(p)) {
                handleSelectDefaultPlaceholder(p);
              } else if (audienceHeaders.includes(p)) {
                setSelectedAudiencePlaceholders((prev) => [...prev, p]);
                onAddVariable(p);
              } else if (existingColumns.includes(p)) {
                setSelectedExistingPlaceholders((prev) => [...prev, p]);
                onAddVariable(p);
              } else if (uploadedColumns.includes(p)) {
                setSelectedCustomVariables((prev) => [...prev, p]);
                onAddVariable(p);
              } else {
                // Fallback: treat as default
                handleSelectDefaultPlaceholder(p);
              }
            });

            removed.forEach((p) => {
              if (selectedDefaultPlaceholders.includes(p)) {
                handleRemoveDefaultPlaceholder(p);
              } else if (selectedAudiencePlaceholders.includes(p)) {
                setSelectedAudiencePlaceholders((prev) => prev.filter((x) => x !== p));
                onRemoveVariable(p);
              } else if (selectedExistingPlaceholders.includes(p)) {
                setSelectedExistingPlaceholders((prev) => prev.filter((x) => x !== p));
                onRemoveVariable(p);
              } else if (selectedCustomVariables.includes(p)) {
                setSelectedCustomVariables((prev) => prev.filter((x) => x !== p));
                onRemoveVariable(p);
              }
            });
          }}
          renderValue={() => {
            // Deduplicate by sanitized name to avoid double-counting
            const allSelected = new Set([
              ...selectedDefaultPlaceholders.map((n) => sanitizeVariableName(n)),
              ...selectedAudiencePlaceholders.map((n) => sanitizeVariableName(n)),
              ...selectedExistingPlaceholders.map((n) => sanitizeVariableName(n)),
              ...selectedCustomVariables.map((n) => sanitizeVariableName(n)),
            ]);
            const totalSelected = allSelected.size;
            if (totalSelected === 0) return <span style={{ color: "#2E3860" }}>Select placeholders</span>;
            return `${totalSelected} selected`;
          }}
          className={classes.select}
          MenuProps={selectMenuProps}
          IconComponent={DropdownChevron}
        >
          {(() => {
            const items: React.ReactNode[] = [];
            const isSelected = (name: string) =>
              selectedDefaultPlaceholders.includes(name) ||
              selectedAudiencePlaceholders.includes(name) ||
              selectedExistingPlaceholders.includes(name) ||
              selectedCustomVariables.includes(name);
            const selectedItemStyle: React.CSSProperties = {
              borderRadius: 4,
              border: "1px solid #2E3860",
              background: "#FFF",
            };
            const checkIcon = (
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.64059 5.08949L2.80059 5.92949L5.50059 8.62949L11.5006 2.62949L10.6606 1.78949L5.50059 6.94949L3.64059 5.08949ZM10.9006 6.22949C10.9006 8.86949 8.74059 11.0295 6.10059 11.0295C3.46059 11.0295 1.30059 8.86949 1.30059 6.22949C1.30059 3.58949 3.46059 1.42949 6.10059 1.42949C6.58059 1.42949 7.00059 1.48949 7.42059 1.60949L8.38059 0.649492C7.66059 0.409492 6.88059 0.229492 6.10059 0.229492C2.80059 0.229492 0.100586 2.92949 0.100586 6.22949C0.100586 9.52949 2.80059 12.2295 6.10059 12.2295C9.40059 12.2295 12.1006 9.52949 12.1006 6.22949H10.9006Z"
                  fill="#57578E"
                />
              </svg>
            );
            const sectionHeaderStyle: React.CSSProperties = {
              color: "#707087",
              fontSize: "11px",
              fontWeight: 600,
              textTransform: "uppercase",
              lineHeight: "32px",
              borderTop: "1px solid #E2E2FC",
              marginTop: "4px",
              opacity: 1,
              pointerEvents: "none",
              minHeight: "auto",
              paddingTop: "4px",
              paddingBottom: 0,
            };

            // 1. Default Placeholders group — only show when no other source is active
            const hasAudienceData = isADM && campaignAudiences.length > 0;
            const hasExistingFile = selectedVariableFile && filePlaceholders.length > 0;
            const hasUploadedFile = !!uploadedFileData;
            const hasOtherSource = hasAudienceData || hasExistingFile || hasUploadedFile;

            if (!hasOtherSource) {
              items.push(
                <MenuItem
                  key="__default_header"
                  disabled
                  style={{ ...sectionHeaderStyle, borderTop: "none", marginTop: 0 }}
                >
                  Default
                </MenuItem>
              );
              DEFAULT_PLACEHOLDERS.forEach((placeholder) => {
                const sel = isSelected(placeholder.name);
                items.push(
                  <MenuItem
                    key={`default-${placeholder.name}`}
                    value={placeholder.name}
                    className={classes.menuItem}
                    style={sel ? selectedItemStyle : undefined}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                      <span style={{ flex: 1 }}>{placeholder.name}</span>
                      {sel && checkIcon}
                    </div>
                  </MenuItem>
                );
              });
            } else {
              // When other sources are active, always show full_name if not already present in any source
              const ALWAYS_SHOW = ["Full Name"];
              const allAudienceHeaders = isADM ? campaignAudiences.flatMap((a) => a.headers) : [];
              const allOtherColumns = [
                ...allAudienceHeaders,
                ...(selectedVariableFile?.csvColumns || []),
                ...(uploadedFileData?.columns || []),
              ].map((c) => c.toLowerCase().replace(/\s+/g, "_"));

              const missingDefaults = ALWAYS_SHOW.filter(
                (name) => !allOtherColumns.includes(name.toLowerCase().replace(/\s+/g, "_"))
              );

              if (missingDefaults.length > 0) {
                items.push(
                  <MenuItem key="__default_header" disabled style={sectionHeaderStyle}>
                    <span>Default</span>
                  </MenuItem>
                );
                missingDefaults.forEach((name) => {
                  const sel = isSelected(name);
                  items.push(
                    <MenuItem
                      key={`default-${name}`}
                      value={name}
                      className={classes.menuItem}
                      style={sel ? { ...selectedItemStyle } : undefined}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                        <span style={{ flex: 1 }}>{name}</span>
                        {sel && checkIcon}
                      </div>
                    </MenuItem>
                  );
                });
              }
            }

            // 2. Audience Placeholders group (ADM only)
            // Show ALL audience file columns. We intentionally do NOT filter out
            // columns that match a DEFAULT_PLACEHOLDER name: when an audience (or
            // any other source) is active, the Default group above is collapsed
            // to just `ALWAYS_SHOW` ("Full Name"), so filtering default-named
            // columns here (First Name, Last Name, Address 1, City, State…) made
            // them disappear from every group entirely.
            const uniqueAudienceHeaders = isADM ? Array.from(new Set(campaignAudiences.flatMap((a) => a.headers))) : [];
            // Still needed by the Existing/Uploaded file groups below for dedup.
            const defaultNames = DEFAULT_PLACEHOLDERS.map((p) => p.name);
            if (uniqueAudienceHeaders.length > 0) {
              const audienceName = campaignAudiences.map((a) => a.name).join(", ");
              const audienceRows = campaignAudiences.reduce((sum, a) => sum + a.rowsAmount, 0);
              items.push(
                <MenuItem key="__audience_header" disabled style={sectionHeaderStyle}>
                  <div style={{ display: "flex", flexDirection: "column", lineHeight: "normal" }}>
                    <span>Audience</span>
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 400,
                        textTransform: "none",
                        color: "#9CA3AF",
                        lineHeight: "14px",
                      }}
                    >
                      {audienceName} · {audienceRows.toLocaleString()} rows
                    </span>
                  </div>
                </MenuItem>
              );
              uniqueAudienceHeaders.forEach((name) => {
                const sel = isSelected(name);
                items.push(
                  <MenuItem
                    key={`audience-${name}`}
                    value={name}
                    className={classes.menuItem}
                    style={sel ? selectedItemStyle : undefined}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                      <span style={{ flex: 1 }}>{name}</span>
                      {sel && checkIcon}
                    </div>
                  </MenuItem>
                );
              });
            }

            // 3. Existing File Placeholders group
            const existingColumns = selectedVariableFile?.csvColumns || [];
            const uniqueExistingColumns = existingColumns.filter(
              (h) => !defaultNames.includes(h) && !uniqueAudienceHeaders.includes(h)
            );
            if (uniqueExistingColumns.length > 0) {
              const existingRowCount =
                selectedVariableFile?.metadata?.rowsAmount || selectedVariableFile?.csvColumns?.length || 0;
              items.push(
                <MenuItem key="__existing_header" disabled style={sectionHeaderStyle}>
                  <div style={{ display: "flex", flexDirection: "column", lineHeight: "normal" }}>
                    <span>{selectedVariableFile?.name || "Existing File"}</span>
                    {existingRowCount > 0 && (
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 400,
                          textTransform: "none",
                          color: "#9CA3AF",
                          lineHeight: "14px",
                        }}
                      >
                        {existingRowCount.toLocaleString()} rows
                      </span>
                    )}
                  </div>
                </MenuItem>
              );
              uniqueExistingColumns.forEach((name) => {
                const sel = isSelected(name);
                items.push(
                  <MenuItem
                    key={`existing-${name}`}
                    value={name}
                    className={classes.menuItem}
                    style={sel ? selectedItemStyle : undefined}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                      <span style={{ flex: 1 }}>{name}</span>
                      {sel && checkIcon}
                    </div>
                  </MenuItem>
                );
              });
            }

            // 4. Uploaded File Placeholders group
            const uploadedColumns = uploadedFileData?.columns || [];
            const uniqueUploadedColumns = uploadedColumns.filter(
              (h) =>
                !defaultNames.includes(h) && !uniqueAudienceHeaders.includes(h) && !uniqueExistingColumns.includes(h)
            );
            if (uniqueUploadedColumns.length > 0) {
              const uploadedRowCount = uploadedFileData?.totalRows || uploadedFileData?.data?.length || 0;
              items.push(
                <MenuItem key="__uploaded_header" disabled style={sectionHeaderStyle}>
                  <div style={{ display: "flex", flexDirection: "column", lineHeight: "normal" }}>
                    <span>{uploadedFileData?.fileName || "Uploaded File"}</span>
                    {uploadedRowCount > 0 && (
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 400,
                          textTransform: "none",
                          color: "#9CA3AF",
                          lineHeight: "14px",
                        }}
                      >
                        {uploadedRowCount.toLocaleString()} rows
                      </span>
                    )}
                  </div>
                </MenuItem>
              );
              uniqueUploadedColumns.forEach((name) => {
                const sel = isSelected(name);
                items.push(
                  <MenuItem
                    key={`uploaded-${name}`}
                    value={name}
                    className={classes.menuItem}
                    style={sel ? selectedItemStyle : undefined}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                      <span style={{ flex: 1 }}>{name}</span>
                      {sel && checkIcon}
                    </div>
                  </MenuItem>
                );
              });
            }

            return items;
          })()}
        </Select>

        {/* All selected placeholder chips (default + audience + existing + uploaded) */}
        {(() => {
          const allChips: { name: string; onRemove: () => void }[] = [];

          // Default placeholder chips
          selectedDefaultPlaceholders.forEach((p) => {
            allChips.push({ name: p, onRemove: () => handleRemoveDefaultPlaceholder(p) });
          });

          // Audience placeholder chips
          selectedAudiencePlaceholders.forEach((p) => {
            if (!allChips.some((c) => c.name === p)) {
              allChips.push({
                name: p,
                onRemove: () => {
                  setSelectedAudiencePlaceholders((prev) => prev.filter((x) => x !== p));
                  onRemoveVariable(p);
                },
              });
            }
          });

          // Existing file placeholder chips
          selectedExistingPlaceholders.forEach((p) => {
            if (!allChips.some((c) => c.name === p)) {
              allChips.push({
                name: p,
                onRemove: () => {
                  setSelectedExistingPlaceholders((prev) => prev.filter((x) => x !== p));
                  onRemoveVariable(p);
                },
              });
            }
          });

          // Uploaded file (custom) placeholder chips
          selectedCustomVariables.forEach((p) => {
            if (!allChips.some((c) => c.name === p)) {
              allChips.push({
                name: p,
                onRemove: () => {
                  setSelectedCustomVariables((prev) => prev.filter((x) => x !== p));
                  onRemoveVariable(p);
                },
              });
            }
          });

          if (allChips.length === 0) return null;

          return (
            <div className="flex flex-wrap gap-2">
              {allChips.map((chip) => (
                <div
                  key={chip.name}
                  onClick={chip.onRemove}
                  className="max-h-7 min-h-7 px-2 py-1 bg-white hover:bg-gray-50 transition-colors duration-200 rounded-lg outline outline-1 outline-offset-[-1px] outline-indigo-100 inline-flex justify-center items-center gap-2 overflow-hidden cursor-pointer"
                >
                  <div className="text-slate-900 text-[10px] font-normal font-['Inter'] leading-normal">
                    {chip.name}
                  </div>
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                    className="hover:scale-110 transition-transform duration-200"
                  >
                    <path
                      d="M4.27045 4.36622C4.56334 4.07332 5.0381 4.07332 5.331 4.36622L8.13276 7.16797L10.8691 4.43262C11.162 4.13973 11.6377 4.13973 11.9306 4.43262C12.2235 4.72552 12.2235 5.20126 11.9306 5.49415L9.19428 8.2295L11.9306 10.9648C12.2235 11.2577 12.2235 11.7335 11.9306 12.0264C11.6377 12.3192 11.162 12.3192 10.8691 12.0264L8.13276 9.29004L5.331 12.0928C5.03811 12.3857 4.56335 12.3857 4.27045 12.0928C3.97756 11.7999 3.97756 11.3251 4.27045 11.0322L7.07221 8.2295L4.27045 5.42676C3.97756 5.13387 3.97757 4.65911 4.27045 4.36622Z"
                      fill="#0C172C"
                    />
                  </svg>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* ============================================================ */}
      {/* SEPARATOR */}
      {/* ============================================================ */}
      <div className={classes.separator} />

      {/* ============================================================ */}
      {/* 2. CUSTOM VARIABLES (drag & drop upload) */}
      {/* ============================================================ */}
      <div className={classes.section}>
        <div className={classes.titleRow}>
          <span className={classes.title}>Custom Placeholders</span>
          <Tooltip
            title={
              <span>
                {
                  "• Add as many columns as you'd like to the CSV and create custom placeholders in the Design Tool to further personalize your direct mail templates."
                }
                <br />
                {"• Each column header will become a placeholder type."}
                <br />
                <br />
                {"Tip: promo_code and qr_link enable per-mailer tracking."}
              </span>
            }
            arrow
            placement="top"
            classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
          >
            <span style={{ display: "inline-flex" }}>
              <InfoIcon />
            </span>
          </Tooltip>
        </div>

        {/* Description, Drop zone, Download Templates - hidden when file uploaded */}
        {!uploadedFileData && (
          <>
            <p
              style={{
                color: "#707087",
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontFamily: '"Inter", sans-serif',
                fontSize: 10,
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "12px",
                margin: 0,
              }}
            >
              Upload a CSV file with your placeholder data. Each column will be available as a placeholder in your
              design template.
            </p>

            {uploadProgress?.active ? (
              /* Upload Progress Indicator */
              <div
                style={{
                  display: "flex",
                  width: 182,
                  height: 82,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    padding: 8,
                    flexDirection: "column",
                    alignItems: "flex-start",
                    flex: "1 0 0",
                    borderRadius: 8,
                    border: "1px solid #E2E2FC",
                    background: "#FFF",
                    gap: 4,
                  }}
                >
                  {/* File name + cancel */}
                  <div style={{ display: "flex", alignItems: "center", width: "100%", gap: 4 }}>
                    <span
                      style={{
                        color: "#2E3860",
                        fontFeatureSettings: "'liga' off, 'clig' off",
                        fontFamily: '"Inter", sans-serif',
                        fontSize: 12,
                        fontWeight: 500,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap" as const,
                      }}
                      title={uploadProgress.fileName}
                    >
                      {uploadProgress.fileName}
                    </span>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      style={{ cursor: "pointer", flexShrink: 0 }}
                      onClick={() => {
                        uploadAbortRef.current?.abort();
                        setUploadProgress(null);
                      }}
                    >
                      <path
                        d="M3 3L11 11M11 3L3 11"
                        stroke="#2E3860"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  {/* File size */}
                  <span
                    style={{
                      color: "#2E3860",
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontFamily: '"Inter", sans-serif',
                      fontSize: 12,
                      fontWeight: 400,
                      lineHeight: "16px",
                    }}
                  >
                    {formatFileSize(uploadProgress.fileSize)}
                  </span>

                  {/* Progress bar + percentage */}
                  <div
                    style={{
                      display: "flex",
                      padding: 8,
                      alignItems: "center",
                      gap: 8,
                      alignSelf: "stretch",
                    }}
                  >
                    {/* Bar track */}
                    <div
                      style={{
                        display: "flex",
                        height: 8,
                        flexDirection: "column",
                        alignItems: "flex-start",
                        flex: "1 0 0",
                        borderRadius: 8,
                        background: "#E2E2FC",
                        overflow: "hidden",
                      }}
                    >
                      {/* Bar fill */}
                      <div
                        style={{
                          width: `${uploadProgress.percent}%`,
                          height: 8,
                          borderRadius: 8,
                          background: "#4F5B94",
                          transition: "width 0.3s ease",
                        }}
                      />
                    </div>
                    {/* Percentage text */}
                    <span
                      style={{
                        color: "#2E3860",
                        textAlign: "right",
                        fontFeatureSettings: "'liga' off, 'clig' off",
                        fontFamily: '"Inter", sans-serif',
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: "16px",
                        minWidth: 32,
                      }}
                    >
                      {uploadProgress.percent}%
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Drop zone - uses native event listeners (like BrandKit) to bypass tldraw's global drag handlers */}
                <div
                  ref={dropZoneRef}
                  data-variables-dropzone="true"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    display: "flex",
                    padding: "8px 16px",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                    alignSelf: "stretch",
                    border: `1px dashed ${dragging ? "#4f5b94" : "#1A2547"}`,
                    borderRadius: 4,
                    cursor: "pointer",
                    background: dragging ? "#f8f9ff" : "transparent",
                    transition: "background 0.15s, border-color 0.15s",
                  }}
                >
                  {/* Cloud upload icon */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                    <g clipPath="url(#clip0_var_dz)">
                      <path
                        d="M32.25 16.325C31.1167 9.85625 26.0667 5 20 5C15.1833 5 11 8.075 8.91667 12.575C3.9 13.175 0 17.9563 0 23.75C0 29.9562 4.48333 35 10 35H31.6667C36.2667 35 40 30.8 40 25.625C40 20.675 36.5833 16.6625 32.25 16.325ZM23.3333 21.875V29.375H16.6667V21.875H11.6667L20 12.5L28.3333 21.875H23.3333Z"
                        fill="#2E3860"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_var_dz">
                        <rect width="40" height="40" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <p
                    style={{
                      color: "#0C172C",
                      textAlign: "center",
                      fontFamily: '"Inter", sans-serif',
                      fontSize: 14,
                      fontWeight: 600,
                      lineHeight: "16px",
                      margin: 0,
                    }}
                  >
                    Drop your .csv or .xlsx here
                  </p>
                  <p
                    style={{
                      color: "#798498",
                      textAlign: "center",
                      fontFamily: '"Inter", sans-serif',
                      fontSize: 12,
                      fontWeight: 400,
                      lineHeight: "12px",
                      margin: 0,
                    }}
                  >
                    Max file size is 2GB
                  </p>
                </div>

                {/* Download Templates */}
                <button onClick={downloadTemplate} className={classes.templateLink}>
                  Download Templates
                </button>
              </>
            )}
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />

        {/* Uploaded file info + variables dropdown + chips */}
        {uploadedFileData && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
            {/* File name with delete icon */}
            <div className={classes.uploadedFileRow}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                <span className={classes.uploadedFileName}>{uploadedFileData.fileName}</span>
                <div
                  onClick={() => {
                    // Smart cleanup: keep placeholders that exist in defaults, remove the rest
                    const defaultNames = DEFAULT_PLACEHOLDERS.map((p) => p.name);
                    const defaultSanitized = new Set(defaultNames.map((n) => sanitizeVariableName(n)));
                    const toRemove: string[] = [];
                    const toKeepAsDefault: string[] = [];

                    selectedCustomVariables.forEach((v) => {
                      const san = sanitizeVariableName(v);
                      if (defaultSanitized.has(san)) {
                        toKeepAsDefault.push(defaultNames.find((d) => sanitizeVariableName(d) === san) || v);
                      } else if (isQrPlaceholder(v)) {
                        // Preserve QR placeholders when removing the uploaded file —
                        // the QR code shape on the canvas should not be deleted.
                      } else {
                        toRemove.push(v);
                      }
                    });

                    toRemove.forEach((v) => onRemoveVariable(v));
                    setSelectedCustomVariables([]);
                    setSelectedExistingPlaceholders([]);
                    // Re-sync defaults from canvas after clearing file
                    // Use timeout to let React process the state updates first
                    setTimeout(() => {
                      if (!getActiveVariablesFromCanvas) return;
                      const activeVars = getActiveVariablesFromCanvas();
                      const defNames = DEFAULT_PLACEHOLDERS.map((p) => p.name);
                      setSelectedDefaultPlaceholders(activeVars.filter((name) => defNames.includes(name)));
                    }, 50);
                    if (toKeepAsDefault.length > 0) {
                      setSelectedDefaultPlaceholders((prev) => {
                        const existing = new Set(prev.map((p) => sanitizeVariableName(p)));
                        const newDefaults = toKeepAsDefault.filter((d) => !existing.has(sanitizeVariableName(d)));
                        return [...prev, ...newDefaults];
                      });
                    }
                    setUploadedFileData(null);
                    setSelectedVariableFile(null);
                  }}
                  style={{ cursor: "pointer", flexShrink: 0 }}
                >
                  <TrashIcon />
                </div>
              </div>
            </div>

            {/* Custom variables are now in the unified Placeholders dropdown above */}
          </div>
        )}
      </div>

      {/* ============================================================ */}
      {/* OR divider */}
      {/* ============================================================ */}
      <div className={classes.orDivider}>or</div>

      {/* ============================================================ */}
      {/* 3. EXISTING VARIABLE FILES */}
      {/* ============================================================ */}
      <div className={classes.section}>
        <div className={classes.titleRow}>
          <span className={classes.title}>Placeholder Files</span>
          <Tooltip
            title="Select a previously uploaded placeholder file to use its columns as placeholders."
            arrow
            placement="top"
            classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
          >
            <span style={{ display: "inline-flex" }}>
              <InfoIcon />
            </span>
          </Tooltip>
        </div>
        <Select
          variant="outlined"
          displayEmpty
          value={dropdownValue}
          onChange={handleDropdownChange}
          renderValue={(value: unknown) => {
            if (!value || value === "" || value === "__uploaded__")
              return <span style={{ color: "#2E3860" }}>Select placeholder file</span>;
            const file = existingVariableFiles.find((f) => String(f.id) === value);
            return file ? file.name : "Select placeholder file";
          }}
          className={classes.select}
          MenuProps={selectMenuProps}
          IconComponent={DropdownChevron}
        >
          {existingVariableFiles.length === 0 && (
            <MenuItem disabled className={classes.fileMenuItem}>
              <span style={{ color: "#798498" }}>No placeholder files found</span>
            </MenuItem>
          )}
          {(selectedVariableFile || uploadedFileData) && (
            <MenuItem value={SELECT_NONE_VALUE} className={classes.fileMenuItem}>
              <span style={{ color: "#798498", fontStyle: "italic" }}>None</span>
            </MenuItem>
          )}
          {existingVariableFiles.map((file) => {
            const fileRows = file.metadata?.rowsAmount || 0;
            const isRowMismatch = audienceRowCount !== null && fileRows > 0 && fileRows !== audienceRowCount;
            return (
              <MenuItem
                key={file.id}
                value={String(file.id)}
                disabled={isRowMismatch}
                className={classes.fileMenuItem}
                style={isRowMismatch ? { opacity: 0.45 } : undefined}
              >
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span>{file.name}</span>
                  <span style={{ fontSize: 10, color: isRowMismatch ? "#E00404" : "#9CA3AF" }}>
                    {file.csvColumns.length} columns
                    {fileRows > 0 && ` · ${fileRows.toLocaleString()} rows`}
                    {isRowMismatch && ` (audience has ${audienceRowCount?.toLocaleString()})`}
                  </span>
                </div>
              </MenuItem>
            );
          })}
        </Select>
      </div>

      {/* Loading */}
      {isLoading && !uploadProgress?.active && (
        <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
          <div className={classes.spinner} />
        </div>
      )}

      {/* Preview thumbnail with overlay button */}
      {hasFileData && (
        <div
          style={{
            position: "relative",
            borderRadius: 0,
            overflow: "hidden",
            border: "1px solid transparent",
            background: "#FAFAFE",
            width: 182,
            minWidth: 182,
            maxWidth: 182,
            height: 128,
            minHeight: 128,
            maxHeight: 128,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {thumbnailUrl && (
            <img
              src={thumbnailUrl}
              alt="Design preview"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                objectPosition: "center",
                filter: "blur(4px)",
                display: "block",
              }}
            />
          )}
          <button
            onClick={() => setShowPreviewModal(true)}
            style={{
              position: "absolute",
              display: "inline-flex",
              minHeight: 32,
              maxHeight: 32,
              padding: "4px 16px",
              justifyContent: "center",
              alignItems: "center",
              gap: 8,
              borderRadius: 12,
              border: "1px solid #1A2547",
              background: "#FFF",
              cursor: "pointer",
              color: "#1A2547",
              textAlign: "center",
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: '"Inter", sans-serif',
              fontSize: 14,
              fontWeight: 700,
              lineHeight: "120%",
            }}
          >
            Preview Placeholder
          </button>
        </div>
      )}

      {/* Column Mapping Modal */}
      <ColumnMappingModal
        open={showMappingModal}
        columns={pendingFileData?.columns || []}
        sampleData={pendingFileData?.data || []}
        onConfirm={handleMappingConfirm}
        onCancel={handleMappingCancel}
      />

      <PreviewVariablesModal
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        data={getCombinedPreviewData()}
        applyPreview={applyPreview}
        clearPreview={clearPreview}
        editor={editor}
        designId={designId}
        campaignId={campaignId}
        format={format}
        onFetchData={
          getCombinedPreviewData().length === 0 && isADM && campaignAudiences.length > 0
            ? fetchAudienceCsvData
            : undefined
        }
        streamingSources={
          uploadedFileData?.csvBlob instanceof File
            ? [{ source: uploadedFileData.csvBlob, headers: uploadedFileData.columns }]
            : undefined
        }
        resolveStreamingSources={async () => {
          const sources: Array<{ source: File | string; headers?: string[] }> = [];
          // Resolve audience CSV signed URL
          if (isADM && campaignAudiences.length > 0 && campaignAudiences[0].csvUrl) {
            try {
              const res = await apiClient.getAudienceDownloadUrl({ url: campaignAudiences[0].csvUrl });
              if (res.ok) {
                const { url } = await res.json();
                sources.push({ source: url, headers: campaignAudiences[0].headers });
              }
            } catch (e) {
              /* fallback to sample data */
            }
          }
          // Resolve existing variable file signed URL
          if (selectedVariableFile?.csvUrl) {
            try {
              const res = await apiClient.getVariableDownloadUrl({ url: selectedVariableFile.csvUrl });
              if (res.ok) {
                const { url } = await res.json();
                sources.push({ source: url, headers: selectedVariableFile.csvColumns });
              }
            } catch (e) {
              /* fallback to sample data */
            }
          }
          return sources;
        }}
      />
    </div>
  );
};

const useStyles = makeStyles({
  panelContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    padding: 0,
    fontFamily: '"Inter", sans-serif',
    background: "#FFF",
    overflowY: "auto",
    maxHeight: "100%",
    scrollbarWidth: "none", // Firefox
    msOverflowStyle: "none", // IE/Edge
    "&::-webkit-scrollbar": {
      display: "none", // Chrome/Safari
    },
  },
  panelTitle: {
    color: "#0C172C",
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontFamily: '"Inter", "Inter", sans-serif',
    fontSize: 20,
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
    margin: 0,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: {
    color: "#0C172C",
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontFamily: '"Inter", sans-serif',
    fontSize: 14,
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "16px",
  },
  // Dropdown: exact same as Settings orientation/format
  select: {
    width: "100%",
    maxHeight: "38px !important",
    borderRadius: "8px !important",
    "& .MuiInputBase-root": {
      height: "38px !important",
      minHeight: "38px !important",
      maxHeight: "38px !important",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E2E2FC !important",
      borderWidth: "1px !important",
      transition: "none !important",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E2E2FC !important",
      borderWidth: "1px !important",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E2E2FC !important",
      borderWidth: "1px !important",
      boxShadow: "none !important",
    },
    "& fieldset": {
      borderColor: "#E2E2FC !important",
      borderWidth: "1px !important",
      transition: "none !important",
    },
    "&:hover fieldset": {
      borderColor: "#E2E2FC !important",
      borderWidth: "1px !important",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#E2E2FC !important",
      borderWidth: "1px !important",
      boxShadow: "none !important",
    },
    "&:hover": {
      backgroundColor: "transparent !important",
    },
    "&.Mui-focused": {
      backgroundColor: "transparent !important",
      boxShadow: "none !important",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px !important",
      fontSize: "12px",
      color: "#2E3860",
      height: "38px !important",
      minHeight: "38px !important",
      maxHeight: "38px !important",
      overflow: "hidden",
    },
    "& .MuiSelect-root": {
      paddingLeft: "12px",
      paddingRight: "32px",
      height: "38px !important",
      minHeight: "38px !important",
      maxHeight: "38px !important",
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      minHeight: "36px !important",
      maxHeight: "36px !important",
      overflow: "hidden",
      fontSize: "12px !important",
      "&:focus": { backgroundColor: "transparent !important" },
      "&:hover": { backgroundColor: "transparent !important" },
      "&:active": { backgroundColor: "transparent !important" },
    },
    "& .MuiSelect-icon": {
      color: "#2E3860",
      right: "8px",
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 12px",
    },
  },
  menuItem: {
    fontSize: "12px !important",
    "&:hover": { backgroundColor: "#E2E2FC" },
    "&.Mui-selected": { backgroundColor: "#E2E2FC" },
    "&.Mui-selected:hover": { backgroundColor: "#E2E2FC" },
  },
  fileMenuItem: {
    fontSize: "12px !important",
    position: "relative",
    paddingRight: "32px",
    "&:hover": { backgroundColor: "#E2E2FC" },
    "&.Mui-selected": {
      backgroundColor: "#FFFFFF !important",
      border: "1px solid #2E3860",
      borderRadius: "4px",
      "&::after": {
        content: '""',
        position: "absolute",
        right: "10px",
        top: "50%",
        transform: "translateY(-50%)",
        width: "12px",
        height: "12px",
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'><path fill-rule='evenodd' clip-rule='evenodd' d='M3.54 4.86L2.7 5.7L5.4 8.4L11.4 2.4L10.56 1.56L5.4 6.72L3.54 4.86ZM10.8 6C10.8 8.64 8.64 10.8 6 10.8C3.36 10.8 1.2 8.64 1.2 6C1.2 3.36 3.36 1.2 6 1.2C6.48 1.2 6.9 1.26 7.32 1.38L8.28 0.42C7.56 0.18 6.78 0 6 0C2.7 0 0 2.7 0 6C0 9.3 2.7 12 6 12C9.3 12 12 9.3 12 6H10.8Z' fill='%2357578E'/></svg>\")",
      },
    },
    "&.Mui-selected:hover": { backgroundColor: "#FFFFFF !important" },
  },
  menuDivider: {
    height: 1,
    backgroundColor: "#E2E2FC",
    margin: "4px 0",
  },
  // Tooltip: exact same as Settings
  tooltip: {
    backgroundColor: "#0C172C",
    color: "#FFFFFF",
    fontFamily: '"Inter", sans-serif',
    fontSize: 14,
    fontWeight: 700,
    lineHeight: "20px",
    maxWidth: 300,
    padding: "8px 16px",
    borderRadius: 4,
  },
  tooltipArrow: {
    color: "#0C172C",
  },
  // Separator between Default Placeholders and Custom Placeholders
  separator: {
    height: 0,
    alignSelf: "stretch",
    borderTop: "1px solid #E2E2FC",
  },
  templateLink: {
    color: "#1A2547",
    textAlign: "center" as const,
    fontFamily: '"Inter", sans-serif',
    fontSize: 14,
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "18px",
    textDecoration: "underline",
    textDecorationStyle: "solid" as const,
    textUnderlineOffset: "auto",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    alignSelf: "stretch",
  },
  uploadedFileRow: {
    display: "flex",
    padding: "12px 8px",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: 16,
    flex: "1 0 0",
    borderRadius: 8,
    border: "1px solid #E2E2FC",
    background: "#FFF",
  },
  uploadedFileName: {
    color: "#2E3860",
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontFamily: '"Inter", sans-serif',
    fontSize: 12,
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "16px",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  orDivider: {
    color: "#2E3860",
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontFamily: '"Inter", sans-serif',
    fontSize: 12,
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "20px",
    textAlign: "center" as const,
  },
  matchingBanner: {
    padding: "8px 12px",
    background: "#F5F5FF",
    borderRadius: 8,
    fontSize: 10,
    fontWeight: 600,
    color: "#57578E",
    fontFamily: '"Inter", sans-serif',
  },
  spinner: {
    width: 20,
    height: 20,
    border: "2px solid #E2E2FC",
    borderTopColor: "#F43F5E",
    borderRadius: "50%",
    animation: "$spin 0.6s linear infinite",
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
});

export default VariablesPanelV2;
